/**
 * App.jsx — Admin Panel root.
 *
 * Route tree:
 *   /login                    → LoginPage         (public)
 *   /                         → redirect → /dashboard
 *   /dashboard                → DashboardPage     (auth required)
 *   /questions                → QuestionsPage     (auth required)
 *   /exams                    → ExamsPage         (auth required)
 *   /schedule                 → SchedulePage      (auth required)
 *   /materials                → MaterialsPage     (auth required)
 *   /forum                    → ForumPage         (auth required)
 *   /settings                 → SettingsPage      (auth required)
 *   /users                    → UsersPage         (admin only)
 */

import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { telegramOidcLogin } from '@/api/auth'
import { hasTelegramOidcCallback, consumeTelegramOidcCallback } from '@/utils/telegramOidc'
import DashboardLayout from '@/layouts/DashboardLayout'
import LoginPage       from '@/pages/LoginPage'
import DashboardPage   from '@/pages/Dashboard/DashboardPage'
import QuestionsPage   from '@/pages/Questions/QuestionsPage'
import ExamsPage       from '@/pages/Exams/ExamsPage'
import CreateExamPage  from '@/pages/Exams/CreateExamPage'
import GroupsPage      from '@/pages/Groups/GroupsPage'
import UsersPage       from '@/pages/Users/UsersPage'
import SchedulePage    from '@/pages/Schedule/SchedulePage'
import MaterialsPage   from '@/pages/Materials/MaterialsPage'
import ForumPage       from '@/pages/Forum/ForumPage'
import SettingsPage    from '@/pages/Settings/SettingsPage'
import SubjectsPage    from '@/pages/Subjects/SubjectsPage'
import TopicsPage      from '@/pages/Topics/TopicsPage'
import QuestionBanksPage from '@/pages/QuestionBanks/QuestionBanksPage'
import WordImportFlow   from '@/components/questions/WordImportFlow'
import GradingCriteriaPage from '@/pages/GradingCriteria/GradingCriteriaPage'
import AnalyticsPage   from '@/pages/Analytics/AnalyticsPage'
import AiLogsPage      from '@/pages/AiLogs/AiLogsPage'
import { Spinner }     from '@/components/ui/primitives'

// ── Guards ────────────────────────────────────────────────────────────
const ALLOWED_ROLES = ['admin', 'teacher']

function RequireAuth() {
  const { isAuth, isLoading, user } = useAuth()
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <Spinner size="lg" />
      </div>
    )
  }
  if (!isAuth) return <Navigate to="/login" replace />
  if (!user?.is_superuser && !ALLOWED_ROLES.includes(user?.role)) return <Navigate to="/login" replace state={{ forbidden: true }} />
  return <Outlet />
}

function RequireAdmin() {
  const { user } = useAuth()
  return (user?.is_superuser || user?.role === 'admin') ? <Outlet /> : <Navigate to="/dashboard" replace />
}

// Intercepts the "Log in with Telegram" OIDC redirect (registered as the app
// root, e.g. https://admin.5mi.uz/) before RequireAuth can bounce an
// unauthenticated visitor to /login and strip the code/state query params.
function TelegramOidcGate({ children }) {
  const { login } = useAuth()
  const [status, setStatus] = useState(() => hasTelegramOidcCallback() ? 'processing' : 'idle')

  useEffect(() => {
    if (status !== 'processing') return
    const callback = consumeTelegramOidcCallback()
    if (!callback) { setStatus('idle'); return }

    telegramOidcLogin(callback.code, `${window.location.origin}/`, callback.verifier)
      .then(({ data }) => {
        const u = data.user
        if (!u.is_superuser && !ALLOWED_ROLES.includes(u.role)) {
          sessionStorage.setItem('tg_oidc_error', 'Bu panelga kirish huquqingiz yo\'q.')
          setStatus('idle')
          return
        }
        login({ access: data.access, refresh: data.refresh, user: u })
        window.location.replace('/dashboard')
      })
      .catch((err) => {
        const detail = err.response?.data?.detail
        sessionStorage.setItem('tg_oidc_error', detail || 'Telegram orqali kirish muvaffaqiyatsiz')
        setStatus('idle')
      })
  }, [status])

  if (status === 'processing') {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <Spinner size="lg" />
      </div>
    )
  }
  return children
}

// ── App ───────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <TelegramOidcGate>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Authenticated */}
          <Route element={<RequireAuth />}>
            <Route element={<DashboardLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard"       element={<DashboardPage />} />
              <Route path="/questions"       element={<QuestionsPage />} />
              <Route path="/exams"           element={<ExamsPage />} />
              <Route path="/exams/create"    element={<CreateExamPage />} />
              <Route path="/analytics"       element={<AnalyticsPage />} />
              <Route path="/groups"          element={<GroupsPage />} />
              <Route path="/topics"          element={<TopicsPage />} />
              <Route path="/question-banks"    element={<QuestionBanksPage />} />
              <Route path="/question-banks/import" element={<WordImportFlow />} />
              <Route path="/grading-criteria" element={<GradingCriteriaPage />} />
              <Route path="/schedule"         element={<SchedulePage />} />
              <Route path="/materials"       element={<MaterialsPage />} />
              <Route path="/forum"           element={<ForumPage />} />
              <Route path="/settings"        element={<SettingsPage />} />

              {/* Admin-only */}
              <Route element={<RequireAdmin />}>
                <Route path="/users"    element={<UsersPage />} />
                <Route path="/subjects" element={<SubjectsPage />} />
                <Route path="/ai-logs"  element={<AiLogsPage />} />
              </Route>
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </TelegramOidcGate>
    </AuthProvider>
  )
}
